import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, Input, ViewChild } from '@angular/core';
import { WidgetService } from 'src/core/services/widget.service';
import { DeviceService } from 'src/core/services/device.service';
import { LoadingService } from 'src/core/services/loading.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { ChatService } from 'src/core/services/chat.service';
import { Table, TableModule } from 'primeng/table';
import { DaterangeService } from 'src/core/services/daterange.service';
import { defer, iif, map, merge, Observable, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { DataService } from 'src/core/services/data.service';
import { selectedWidget, workareaSize } from 'src/core/types/workarea.types';
import { NgShuiModule } from '@shui/angular';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { PartsData, SitePartsResponse } from 'src/core/types/servicehistory.types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiResponse } from 'src/core/types/general.types';
import { SortEvent } from 'primeng/api';
import { SelectedContentService } from 'src/core/services/selectedcontent.service';
import { environment } from 'src/environments/environment';
import { shouldMarkPromptAsOptionAnswer } from 'src/core/utils/chat-option-answer.util';

@Component({
  selector: 'app-spare-parts',
  imports: [CommonModule, TableModule, NgShuiModule, FormsModule, TranslocoModule],
  templateUrl: './spare-parts.component.html',
  styleUrl: './spare-parts.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SparePartsComponent {

  //Table Variables
  @ViewChild('table') table: Table;
  readonly businessPartnerMode = environment.businessPartnerMode;

  constructor(
    private widgetService: WidgetService,
    private deviceService: DeviceService,
    private loadingService: LoadingService,
    private selectedSystemService: SelectedsystemService,
    private chatService: ChatService,
    private daterangeService: DaterangeService,
    private dataService: DataService,
    private selectedContentService: SelectedContentService,
    private translocoService: TranslocoService
  ) { }



  screenInfo$ = this.deviceService.screenInfo$;
  isLoading: boolean;
  isError: boolean;
  isDeviceUI: boolean;
  deviceInformation: any;
  searchTerm: string = '';
  parts: any[] = [];
  selectedRange: number;

  @Input() tabs: any[] = [];
  @ViewChild('tooltip') tooltip: any;
  selectedWidget: string = "None";
  displayedTabs: any[] = [];
  activeTab: string = "Activities";
  workareaSize: workareaSize = "small";
  onMaximize = this.widgetService.onMaximize.bind(this.widgetService);
  onMinimize = this.widgetService.onMinimize.bind(this.widgetService);
   onExpand = (event: any) => {
    if(this.workareaSize !== "fullscreen") {
      this.widgetService.onExpand();
    } else {
      this.widgetService.$workareaSize.next("large");
    }
  }
  switchTab = this.widgetService.switchTab.bind(this.widgetService);
  changeRange = (range: number, type: 'systemHealth' | 'serviceHistory') => {
    this.daterangeService.changeRange(range, type);
  };
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.loadingService.$isSparePartsLoading.subscribe((loading) => {
      this.isLoading = loading;
    });
    this.loadingService.$isSparePartsError.subscribe((error) => {
      this.isError = error;
    });

    //Check which tabs to display
    this.widgetService.$selectedWidget.subscribe({
      next: (widget: selectedWidget) => {
        this.selectedWidget = widget;
        this.displayedTabs = this.tabs.filter(tab => tab.selectedWidget === widget);

      }
    });

    //Check which tab is currently active
    this.widgetService.$activeTab.subscribe((tab) => {
      if (tab) {
        this.activeTab = tab;
      }
      else {
        this.activeTab = this.displayedTabs[0]?.name;
      }
    });

    //Need to know the current workarea size as well to display correct icons
    this.widgetService.$workareaSize.subscribe({
      next: (size: workareaSize) => {
        this.workareaSize = size;
      }
    });

    //Listening to changed Daterange
    const range$ = this.daterangeService.$selectedRangeServiceHistory;

    // Trigger when range changes (normal), or when user clicks refresh (force fetch)
    const triggers$ = merge(
      range$.pipe(map(rangeValue => ({ rangeValue, force: false }))),
      this.selectedSystemService.$refreshParts.pipe(
        withLatestFrom(range$),
        map(([, rangeValue]) => ({ rangeValue, force: true }))
      )
    );

    triggers$.pipe(
      tap(() => {
        // ✅ Reset before API call
        this.loadingService.$isSparePartsLoading.next(true);
        this.loadingService.$isSparePartsError.next(false);
        this.parts = null;
      }),
      switchMap(({ rangeValue, force }) => defer(() => {
        const now = new Date();
        const lastPulled = this.selectedSystemService.PartsPullDate;
        const pulledFor = this.selectedSystemService.partsPulledFor;

        const isDevice = this.selectedContentService.selection.kind == "device";
        this.isDeviceUI = isDevice;
        const serial = this.selectedContentService.currentSystem?.SerialNumber || '';
        const material = this.selectedContentService.currentSystem?.MaterialNumber || '';
        const funcloc = this.selectedContentService.currentSite?.Id;

        const cacheKey = isDevice ? (serial + material) : funcloc;
        const shouldFetch = force ||
          !lastPulled ||
          (now.getTime() - new Date(lastPulled).getTime()) > 5 * 60 * 1000 ||
          pulledFor !== cacheKey;

        type PartsGeneralResponse = PartsData | SitePartsResponse;

        const apiCall$: Observable<ApiResponse<PartsGeneralResponse>> = isDevice
          ? this.dataService.getPartsUsed(material, serial).pipe(
            map((result): ApiResponse<PartsGeneralResponse> => result)
          )
          : this.dataService.getPartsUsedSite(funcloc).pipe(
            map((result): ApiResponse<PartsGeneralResponse> => result)
          );


        return iif(
          () => shouldFetch,
          apiCall$.pipe(
            tap(({ response, status }: ApiResponse<PartsGeneralResponse>) => {
              console.log(force ? 'Forced refresh of parts data' : 'Fetching new parts data');
              this.selectedSystemService.PartsPullDate = now;
              this.selectedSystemService.partsPulledFor = cacheKey;

              if (status === 200) {
                this.selectedSystemService.$spareparts.next(response);
              }
              else if (status === 204) {
                this.selectedSystemService.$spareparts.next(null);
                this.loadingService.$isSparePartsEmpty.next(true);
              }
            })
          ),
          this.selectedSystemService.$spareparts.pipe(
            take(1),
            tap(() => console.log('Using cached parts data')),
            map((cached): ApiResponse<PartsGeneralResponse> => ({
              response: cached,
              status: 200 // or some fixed value to simulate a successful cached response
            }))
          )
        ).pipe(
          map(({ response, status }: ApiResponse<PartsGeneralResponse>) => ({ response, rangeValue }))
        );
      })),
      // 👇 Auto-unsubscribe when the component is destroyed
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      {
        next: ({ response, rangeValue }) => {

          this.selectedRange = rangeValue;

          if (!response) {
            this.parts = [];
            this.loadingService.$isSparePartsEmpty.next(true);
            this.loadingService.$isSparePartsLoading.next(false);
            return;
          }

          if (this.selectedContentService.selection.kind == "device") {

            let temp = response as PartsData;

            const inter = temp?.parts?.filter(f => f.time_window !== null)?.map(part => ({
              ...part,
              UsedTooEarly: this.calculateUsedTooEarly(part.movement_date, part.days_since_last_used, part.median_lifetime),
            })) || [];

            this.parts = inter.filter(f => f.time_window.some(tw => tw === rangeValue));

            this.loadingService.$isSparePartsLoading.next(false);

          }
          else {

            let temp = response as SitePartsResponse;
            const mapped = (temp.parts ?? []).map(
                part => ({
                  ...part,
                  UsedTooEarly: this.calculateUsedTooEarly(part.movement_date, part.days_since_last_used, part.median_lifetime),
                })
              );
            

            const inter = mapped?.filter(f => f.time_window !== null) || [];

            this.parts = inter.filter(f => f.time_window.some(tw => tw === rangeValue));

            this.loadingService.$isSparePartsLoading.next(false);
          }

        },
        error: (error) => {
          console.error('Error fetching spare parts data:', error);
          this.parts = [];
          this.loadingService.$isSparePartsError.next(true);
          this.loadingService.$isSparePartsLoading.next(false);
        }
      }
    );
  }

  onHelpHover(event: MouseEvent) {
    this.tooltip.nativeElement.target = event.target;
    this.tooltip.nativeElement.setAttribute('visible', 'true');
  }
  handleMouseLeave(event: MouseEvent) {
    this.tooltip.nativeElement.removeAttribute('visible');
  }

  calculateUsedTooEarly(exchangedate: string | Date, daysSinceLastUsed: number, medianLifetime: number
  ): boolean {
    const exchangeDateObj = new Date(exchangedate);

    // Step 1: Calculate when it was last used
    const lastUsedDate = new Date(exchangeDateObj);
    lastUsedDate.setDate(lastUsedDate.getDate() - daysSinceLastUsed);

    // Step 2: Calculate the earliest allowed change date
    const earliestChangeDate = new Date(lastUsedDate);
    earliestChangeDate.setDate(earliestChangeDate.getDate() + medianLifetime);

    // Step 3: Compare the actual exchange date to the earliest allowed date
    return exchangeDateObj < earliestChangeDate;
  }



  askChat(part: string) {
    this.widgetService.$chatPipSize.next('maximized');
    const prompt = this.translocoService.translate('spareParts.prompt.replacePart', {
      partName: part['part']
    });
    const isOptionAnswer = shouldMarkPromptAsOptionAnswer(this.chatService.messages?.value);

    this.dataService.searchExact(part["material_number"], part["serial_number"]).subscribe({
      next: (res) => {
        console.log(res);
        this.chatService.addMessage(
          prompt,
          true,
          res.response.ProductFamily,
          isOptionAnswer ? true : null,
          { material: part["material_number"], serial: part["serial_number"] }
        );
      }
    })


  }

  onSearchApply(event: any) {
    this.searchTerm = event.value?.toLowerCase() || '';
  }

  highlight(text: string): string {
    if (!this.searchTerm || !text) return text;

    const escaped = this.searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    return text.replace(regex, '<mark class="hl">$1</mark>');
  }

  retrieveTotalQuantity() {
    const data = this.table?.filteredValue || this.parts;
    return data?.reduce((total, part) => {
      return total + (part.quantity || 0);
    }, 0) || 0;
  }

  retrieveTotalCost() {
    const data = this.table?.filteredValue || this.parts;
    const total = data?.reduce((sum, part) => {
      return sum + part.cost;
    }, 0) || 0;

    return total.toFixed(2) + " €"; // Optionally format with 2 decimal places
  }

  refresh() {
    this.selectedSystemService.$refreshParts.next();
  }

  customSort(event: SortEvent) {
    const field = event.field;
    const order = event.order;

    event.data.sort((a, b) => {
      let val1: any;
      let val2: any;

      switch (field) {
        case 'date': // assuming this is a date field
          val1 = new Date(a.movement_date);
          val2 = new Date(b.movement_date);
          break;

        case 'serial': // string field
          val1 = a.serial_number?.toLowerCase() ?? '';
          val2 = b.serial_number?.toLowerCase() ?? '';
          break;
        
        case 'part': // string field
          val1 = a.part?.toLowerCase() ?? '';
          val2 = b.part?.toLowerCase() ?? '';
          break;
        case 'noti': // string field
          val1 = a.noti_id?.toLowerCase() ?? '';
          val2 = b.noti_id?.toLowerCase() ?? '';
          break;

        case 'life':
          val1 = a.median_lifetime || 0;
          val2 = b.median_lifetime || 0;
          break;
        case 'since':
          val1 = a.days_since_last_used || 0;
          val2 = b.days_since_last_used || 0;
          break;
        case 'quantity':
          val1 = a.quantity || 0;
          val2 = b.quantity || 0;
          break;
        case 'cost':
          val1 = a.cost || 0;
          val2 = b.cost || 0;
          break;

        default:
          val1 = a[field];
          val2 = b[field];
          break;
      }

      let result = 0;
      if (val1 < val2) result = -1;
      else if (val1 > val2) result = 1;

      return result * order;
    });
  }

}
